import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Users, Search, Download, CheckCircle2, Clock, Video, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { RoninGeoDashboard } from "@/components/admin/RoninGeoDashboard";
import { RoninLeadDrawer } from "@/components/admin/RoninLeadDrawer";

function ZoomOfferConfigCard() {
  const [cfg, setCfg] = useState(null);
  const [pct, setPct] = useState("45");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/zoom-offer/config");
        setCfg(data);
        setPct(String(data.discount_pct));
      } catch { setCfg({ enabled: true, discount_pct: 45 }); setPct("45"); }
    })();
  }, []);

  const save = async (next) => {
    setSaving(true);
    try {
      const { data } = await api.patch("/admin/zoom-offer/config", next);
      setCfg(data);
      setPct(String(data.discount_pct));
      toast.success("Offer settings saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't save");
    } finally { setSaving(false); }
  };

  const toggle = (enabled) => save({ enabled });
  const savePct = () => {
    const n = parseInt(pct, 10);
    if (!n || n < 1 || n > 100) { toast.error("Enter a discount between 1 and 100"); return; }
    save({ discount_pct: n });
  };

  if (!cfg) return null;
  return (
    <div className="border border-[#27272A] bg-[#0F0F0F] p-5" data-testid="zoom-offer-config">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="h-9 w-9 rounded-sm bg-[#FF331F]/10 flex items-center justify-center shrink-0">
            <Video className="h-4 w-4 text-[#FF331F]" />
          </div>
          <div>
            <h2 className="text-white font-bold uppercase tracking-wide text-sm">Live Lesson Offer Prompt</h2>
            <p className="text-xs text-zinc-400 mt-1 max-w-md leading-relaxed">
              In-app pop-up offering a discounted 1-on-1 Zoom lesson (shown on the 2nd &amp; 10th app open to non-buyers). Turn it off to pause, or change the discount for a seasonal promo.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-[10px] uppercase tracking-widest font-bold ${cfg.enabled ? "text-emerald-400" : "text-zinc-500"}`}>
            {cfg.enabled ? "Live" : "Paused"}
          </span>
          <Switch checked={cfg.enabled} onCheckedChange={toggle} disabled={saving} data-testid="zoom-offer-toggle" />
        </div>
      </div>
      <div className="flex items-end gap-3 mt-5 pt-4 border-t border-[#27272A]">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold block mb-1.5">Discount %</label>
          <div className="flex items-center gap-1">
            <Input
              type="number" min="1" max="100" value={pct}
              onChange={(e) => setPct(e.target.value)}
              className="w-24 bg-[#050505] border-[#27272A] text-white rounded-sm"
              data-testid="zoom-offer-pct-input"
            />
            <span className="text-zinc-400 text-lg font-bold">%</span>
          </div>
        </div>
        <Button
          onClick={savePct} disabled={saving}
          className="bg-[#FF331F] hover:bg-[#FF5242] text-white uppercase tracking-widest font-bold text-[10px]"
          data-testid="zoom-offer-save-pct"
        >
          {saving ? <Loader2 className="h-3 w-3 mr-2 animate-spin" /> : null} Save discount
        </Button>
      </div>
    </div>
  );
}

export default function RoninLeads() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [selectedLeadId, setSelectedLeadId] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/ronin-leads");
        setLeads(data || []);
      } catch { toast.error("Failed to load leads"); }
      finally { setLoading(false); }
    })();
  }, []);

  const filtered = leads.filter((l) => {
    if (!q.trim()) return true;
    const t = q.toLowerCase();
    return (l.email || "").toLowerCase().includes(t)
      || (l.first_name || "").toLowerCase().includes(t)
      || (l.last_name || "").toLowerCase().includes(t)
      || (l.phone || "").toLowerCase().includes(t)
      || (l.zip_code || "").toLowerCase().includes(t);
  });

  const fmtPref = (val) => (val ? String(val).replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "");
  const fmtFreq = (val) => ({ "2-3": "2–3× a week", "4-5": "4–5× a week", daily: "Every day" }[val] || fmtPref(val));

  const downloadCsv = () => {
    const rows = [["First", "Last", "Email", "Phone", "Country", "ZIP", "Source", "Level", "Goal", "Frequency", "Marketing opt-in", "Verified", "Verified at", "Signed up"]];
    filtered.forEach((l) => {
      const tp = l.training_prefs || {};
      rows.push([
        l.first_name || "", l.last_name || "", l.email || "", l.phone || "", l.country || "", l.zip_code || "",
        (l.signup_source || "").replace(/_/g, " "),
        fmtPref(tp.level), fmtPref(tp.goal), fmtFreq(tp.frequency),
        l.marketing_consent ? "Yes" : "No",
        l.email_verified ? "Yes" : "No",
        l.email_verified_at || "",
        l.created_at || "",
      ]);
    });
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `ronin-box-leads-${Date.now()}.csv`;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    toast.success(`Exported ${filtered.length} lead${filtered.length === 1 ? "" : "s"} to CSV`);
  };

  const verifiedCount = leads.filter((l) => l.email_verified).length;

  return (
    <div className="p-6 md:p-10 space-y-6" data-testid="admin-ronin-leads-page">
      <header className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-[#FF331F] font-bold">Ronin Box</p>
          <h1 className="font-display text-3xl text-white uppercase tracking-tight">Captured Leads</h1>
          <p className="text-sm text-zinc-400 mt-1">{verifiedCount} verified · {leads.length - verifiedCount} pending · {leads.length} total. Marketing opt-in is required, so everyone is on the drip.</p>
        </div>
        <Button onClick={downloadCsv} variant="outline" className="border-[#27272A] bg-[#0F0F0F] text-zinc-300 hover:text-white uppercase tracking-widest font-bold text-xs" data-testid="ronin-leads-export">
          <Download className="h-4 w-4 mr-2" /> Export CSV
        </Button>
      </header>

      <div className="relative max-w-md">
        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, phone, ZIP…" className="pl-9 bg-[#050505] border-[#27272A] text-white rounded-sm" data-testid="ronin-leads-search" />
      </div>

      <ZoomOfferConfigCard />

      <RoninGeoDashboard />

      {loading ? (
        <div className="text-sm text-zinc-500">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="border border-dashed border-[#27272A] p-12 text-center text-zinc-500">
          <Users className="h-8 w-8 mx-auto mb-2 text-zinc-700" /> No leads yet — share /ronin-box to drive signups.
        </div>
      ) : (
        <div className="border border-[#27272A] overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#0F0F0F] border-b border-[#27272A] text-[10px] uppercase tracking-widest text-zinc-500">
              <tr>
                <th className="p-3 text-left">Name</th>
                <th className="p-3 text-left">Email</th>
                <th className="p-3 text-left">Phone</th>
                <th className="p-3 text-left">Country</th>
                <th className="p-3 text-left">ZIP</th>
                <th className="p-3 text-left">Verified</th>
                <th className="p-3 text-left">Signed up</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((l) => (
                <tr
                  key={l.id}
                  onClick={() => setSelectedLeadId(l.id)}
                  className="border-b border-[#27272A] hover:bg-[#0F0F0F] cursor-pointer"
                  data-testid={`ronin-lead-row-${l.id}`}
                >
                  <td className="p-3 text-white font-bold">{l.first_name} {l.last_name}</td>
                  <td className="p-3 text-zinc-300">{l.email}</td>
                  <td className="p-3 text-zinc-400 text-xs">{l.phone || "—"}</td>
                  <td className="p-3 text-zinc-500 text-xs font-mono">{l.country || "—"}</td>
                  <td className="p-3 text-zinc-400 text-xs font-mono">{l.zip_code || "—"}</td>
                  <td className="p-3">
                    {l.email_verified
                      ? <span className="inline-flex items-center gap-1 text-emerald-400 text-[10px] uppercase tracking-widest font-bold"><CheckCircle2 className="h-3 w-3" />Yes</span>
                      : <span className="inline-flex items-center gap-1 text-amber-400 text-[10px] uppercase tracking-widest font-bold"><Clock className="h-3 w-3" />Pending</span>}
                  </td>
                  <td className="p-3 text-zinc-600 text-[11px]">{l.created_at ? new Date(l.created_at).toLocaleDateString() : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <RoninLeadDrawer
        leadId={selectedLeadId}
        open={!!selectedLeadId}
        onClose={() => setSelectedLeadId(null)}
      />
    </div>
  );
}
